import type { ReactNode } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldContent, FieldDescription, FieldLabel, FieldTitle } from "@/components/ui/field";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * `aria-labelledby` is not decoration: Radix draws a `<button role="checkbox">`, which a `<label>`
 * cannot name, so without it a screen reader reads an unnamed checkbox. The wrapping label is
 * what makes the card clickable, which is why both are here.
 */
export function CheckboxCard({
  id,
  title,
  description,
  checked,
  disabled,
  onCheckedChange,
}: {
  id: string;
  title: ReactNode;
  description?: ReactNode;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: () => void;
}) {
  return (
    <FieldLabel htmlFor={id}>
      <Field orientation="horizontal">
        <Checkbox
          id={id}
          checked={checked}
          disabled={disabled}
          onCheckedChange={onCheckedChange}
          aria-labelledby={`${id}-titel`}
          aria-describedby={description ? `${id}-uitleg` : undefined}
        />
        <FieldContent>
          <FieldTitle id={`${id}-titel`}>{title}</FieldTitle>
          {description && <FieldDescription id={`${id}-uitleg`}>{description}</FieldDescription>}
        </FieldContent>
      </Field>
    </FieldLabel>
  );
}

/**
 * One of those cards before the options behind it have arrived. Same border, same padding, same
 * two lines of content beside the box, so ticking starts where the greys were.
 */
export function CheckboxCardSkeleton({ description = true }: { description?: boolean }) {
  return (
    <div className="flex w-full gap-3 rounded-md border p-4">
      <Skeleton className="mt-px size-4 rounded-sm" />
      {/* Sized in `em` of their own type: the title and description set different leading. */}
      <span className="flex flex-1 flex-col gap-1.5">
        <span className="flex h-[1.375em] items-center text-sm">
          <Skeleton className="h-3.5 w-40" />
        </span>
        {description && (
          <span className="flex h-[1.5em] items-center text-sm">
            <Skeleton className="h-3.5 w-56" />
          </span>
        )}
      </span>
    </div>
  );
}
